import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout';
import { Toast } from '../../components';
import { useToast } from '../../hooks/useToast';
import { carService, voyageService } from '../../services';
import { Car, Voyage } from '../../types';
import { attachVoyageCodes, createVoyageCodeMap } from '../../utils/carUtils';

interface VoyageStatisticsRow {
  id: string;
  voyageId: string | null;
  voyage: string;
  shipperCode: string;
  shipperName: string;
  total: number;
  collect: number;
  prepaid: number;
  car: number;
  van: number;
  bigVan: number;
  truckM: number;
}

const initialTotals: Omit<VoyageStatisticsRow, 'id' | 'voyageId' | 'voyage' | 'shipperCode' | 'shipperName'> = {
  total: 0,
  collect: 0,
  prepaid: 0,
  car: 0,
  van: 0,
  bigVan: 0,
  truckM: 0,
};

type SortKey = keyof VoyageStatisticsRow;
type SortDir = 'asc' | 'desc';

const StatisticsVoyagePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [rows, setRows] = useState<VoyageStatisticsRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [sortKey, setSortKey] = useState<SortKey>('voyage');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const { toast, showToast, hideToast } = useToast();
  const isLoadingRef = useRef(false);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      const cmp = typeof aVal === 'string' 
        ? aVal.localeCompare(bVal as string)
        : (aVal as number) - (bVal as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  useEffect(() => {
    const loadStatistics = async () => {
      if (isLoadingRef.current) return;
      
      try {
        isLoadingRef.current = true;
        setLoading(true);
        const carsData = (await carService.getAll({ limit: 1000 })) as Car[];

        const needsLookup = carsData.filter(
          (car) => !car.voyage?.voyage_code && !car.voyage_code && car.voyage_id
        );

        let voyageCodeMap = new Map<string, string>();
        if (needsLookup.length > 0) {
          try {
            const voyages = (await voyageService.getAll({ limit: 1000 })) as Voyage[];
            voyageCodeMap = createVoyageCodeMap(voyages);
          } catch (lookupError) {
            console.error('Error loading voyages for code lookup:', lookupError);
          }
        }

        const normalizedCars = attachVoyageCodes(carsData, voyageCodeMap);
        const aggregated = aggregateStatistics(normalizedCars);
        setRows(aggregated);
      } catch (error) {
        console.error('Error loading voyage statistics:', error);
        showToast('Failed to load voyage statistics', 'error');
      } finally {
        isLoadingRef.current = false;
        setLoading(false);
      }
    };

    loadStatistics();
  }, [showToast]);

  const totalsRow = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        total: acc.total + row.total,
        collect: acc.collect + row.collect,
        prepaid: acc.prepaid + row.prepaid,
        car: acc.car + row.car,
        van: acc.van + row.van,
        bigVan: acc.bigVan + row.bigVan,
        truckM: acc.truckM + row.truckM,
      }),
      { ...initialTotals }
    );
  }, [rows]);

  const handleRowDoubleClick = (row: VoyageStatisticsRow) => {
    if (!row.voyageId) {
      showToast('This entry is not tied to a voyage yet.', 'info');
      return;
    }

    navigate(`/voyage-details/${row.voyageId}`, {
      state: {
        from: {
          pathname: location.pathname,
        },
      },
    });
  };

  return (
    <Layout fullWidth>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100">Statistics Voyage</h1>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Voyage Breakdown</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-white dark:bg-gray-800">
                <tr>
                  <HeaderCell align="left" sortKey="voyage" currentSort={sortKey} sortDir={sortDir} onSort={handleSort}>Voyage</HeaderCell>
                  <HeaderCell align="left" sortKey="shipperCode" currentSort={sortKey} sortDir={sortDir} onSort={handleSort}>Code</HeaderCell>
                  <HeaderCell align="left" sortKey="shipperName" currentSort={sortKey} sortDir={sortDir} onSort={handleSort}>Shipper</HeaderCell>
                  <HeaderCell align="right" sortKey="total" currentSort={sortKey} sortDir={sortDir} onSort={handleSort}>Total</HeaderCell>
                  <HeaderCell align="right" sortKey="collect" currentSort={sortKey} sortDir={sortDir} onSort={handleSort}>Collect</HeaderCell>
                  <HeaderCell align="right" sortKey="prepaid" currentSort={sortKey} sortDir={sortDir} onSort={handleSort}>Prepaid</HeaderCell>
                  <HeaderCell align="right" sortKey="car" currentSort={sortKey} sortDir={sortDir} onSort={handleSort}>Car</HeaderCell>
                  <HeaderCell align="right" sortKey="van" currentSort={sortKey} sortDir={sortDir} onSort={handleSort}>Van</HeaderCell>
                  <HeaderCell align="right" sortKey="bigVan" currentSort={sortKey} sortDir={sortDir} onSort={handleSort}>Big Van</HeaderCell>
                  <HeaderCell align="right" sortKey="truckM" currentSort={sortKey} sortDir={sortDir} onSort={handleSort}>Truck/m</HeaderCell>
                </tr>
              </thead>

              {loading ? (
                <tbody>
                  <tr>
                    <td colSpan={10} className="px-6 py-16 text-center text-sm text-gray-500 dark:text-gray-400">
                      Loading voyage statistics...
                    </td>
                  </tr>
                </tbody>
              ) : rows.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={10} className="px-6 py-16 text-center text-sm text-gray-500 dark:text-gray-400">
                      No voyage statistics available yet.
                    </td>
                  </tr>
                </tbody>
              ) : (
                <>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                    {sortedRows.map((row) => (
                      <tr
                        key={row.id}
                        onDoubleClick={() => handleRowDoubleClick(row)}
                        className="group cursor-pointer transition-colors even:bg-gray-50 dark:even:bg-gray-900 hover:bg-blue-50 dark:hover:bg-blue-900/40"
                        title={row.voyageId ? 'Double-click to view voyage details' : 'No voyage assigned'}
                      >
                        <BodyCell>{row.voyage}</BodyCell>
                        <BodyCell>{row.shipperCode}</BodyCell>
                        <BodyCell>{row.shipperName}</BodyCell>
                        <BodyCell align="right">{row.total}</BodyCell>
                        <BodyCell align="right">{row.collect}</BodyCell>
                        <BodyCell align="right">{row.prepaid}</BodyCell>
                        <BodyCell align="right">{row.car}</BodyCell>
                        <BodyCell align="right">{row.van}</BodyCell>
                        <BodyCell align="right">{row.bigVan}</BodyCell>
                        <BodyCell align="right">{row.truckM}</BodyCell>
                      </tr>
                    ))}
                  </tbody>

                  <tfoot className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                    <tr>
                      <FooterCell>Total</FooterCell>
                      <FooterCell />
                      <FooterCell />
                      <FooterCell align="right">{totalsRow.total}</FooterCell>
                      <FooterCell align="right">{totalsRow.collect}</FooterCell>
                      <FooterCell align="right">{totalsRow.prepaid}</FooterCell>
                      <FooterCell align="right">{totalsRow.car}</FooterCell>
                      <FooterCell align="right">{totalsRow.van}</FooterCell>
                      <FooterCell align="right">{totalsRow.bigVan}</FooterCell>
                      <FooterCell align="right">{totalsRow.truckM}</FooterCell>
                    </tr>
                  </tfoot>
                </>
              )}
            </table>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={hideToast}
        />
      )}
    </Layout>
  );
};

const aggregateStatistics = (cars: Car[]): VoyageStatisticsRow[] => {
  const aggregation = new Map<string, VoyageStatisticsRow>();

  // Filter out related cars (cars with attachment_status)
  const mainCars = cars.filter(car => !car.attachment_status);

  mainCars.forEach((car) => {
    const voyageId = car.voyage_id ?? null;
    const voyageCode = car.voyage?.voyage_code || car.voyage_code || 'Unassigned';
    const shipperCode = car.shipper?.code || 'N/A';
    const shipperName = car.shipper?.shortname || car.shipper?.name || 'Unknown Shipper';
    const key = `${voyageId ?? 'unassigned'}__${shipperCode}`;

    if (!aggregation.has(key)) {
      aggregation.set(key, {
        id: key,
        voyageId,
        voyage: voyageCode,
        shipperCode,
        shipperName,
        total: 0,
        collect: 0,
        prepaid: 0,
        car: 0,
        van: 0,
        bigVan: 0,
        truckM: 0,
      });
    }

    const entry = aggregation.get(key)!;
    entry.total += 1;
    entry.car += 1;

    if (car.prepaid_or_collect === 'C') {
      entry.collect += 1;
    }

    if (car.prepaid_or_collect === 'P') {
      entry.prepaid += 1;
    }
  });

  return Array.from(aggregation.values()).sort((a, b) => {
    if (a.voyage === b.voyage) {
      return a.shipperName.localeCompare(b.shipperName);
    }

    return a.voyage.localeCompare(b.voyage);
  });
};

interface TableCellProps {
  align?: 'left' | 'right';
  children?: React.ReactNode;
}

interface HeaderCellProps extends TableCellProps {
  sortKey?: SortKey;
  currentSort?: SortKey;
  sortDir?: SortDir;
  onSort?: (key: SortKey) => void;
}

const HeaderCell: React.FC<HeaderCellProps> = ({ align = 'left', children, sortKey, currentSort, sortDir, onSort }) => {
  const isActive = sortKey === currentSort;
  return (
    <th
      scope="col"
      onClick={() => sortKey && onSort?.(sortKey)}
      className={`px-6 py-3 text-[11px] font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300 cursor-pointer select-none hover:bg-gray-50 dark:hover:bg-gray-700 border-r border-gray-200 dark:border-gray-600 last:border-r-0 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {isActive && <span>{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );
};

const FooterCell: React.FC<TableCellProps> = ({ align = 'left', children }) => (
  <th
    scope="col"
    className={`px-6 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0 ${
      align === 'right' ? 'text-right' : 'text-left'
    }`}
  >
    {children}
  </th>
);

const BodyCell: React.FC<TableCellProps> = ({ align = 'left', children }) => (
  <td
    className={`px-6 py-4 text-sm text-gray-900 dark:text-gray-100 border-r border-gray-200 dark:border-gray-600 last:border-r-0 ${
      align === 'right' ? 'text-right tabular-nums font-medium' : 'text-left'
    }`}
  >
    {children}
  </td>
);

export default StatisticsVoyagePage;
